import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { Progress } from 'semantic-ui-react'
import axios, { CancelTokenSource } from 'axios'
import { ClimaProgramService } from '~/api/services/climaProgram'
import notify from '~/components/Notify'
import { useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import Layout from '~/components/Layout'
import { El } from '~/components/Layout/style'
import { RouteTypes } from '~/routes/constants'
import { handleErrors } from '~/utils/helpers'
import { createPurchaseFormData, FeedPurchaseData } from './utils'
import FormPart from './FormPart'
import ClimaProgramQuery from '~/QueryHooks/ClimaProgramQuery'
import dayjs from 'dayjs'
import { useHash } from '~/components/useHash'

const filterKeys = ['year', 'search', 'feedProducts', 'status', 'createdAtFrom', 'createdAtTo', 'purchaseDateFrom', 'purchaseDateTo', 'feedingFrom', 'feedingTo'] as const

const EditFeedPurchases: React.FC = () => {
  const { t } = useTranslation()
  const { id, idFarmer } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [hash] = useHash()

  const buildCloseUrl = () => {
    const params = new URLSearchParams()
    filterKeys.forEach(key => {
      const value = searchParams.get(key)
      if (value) params.set(key, value)
    })
    const query = params.toString()
    return query ? `${RouteTypes.CLIMA_PROGRAM_FEED_PURCHASES}?${query}` : RouteTypes.CLIMA_PROGRAM_FEED_PURCHASES
  }

  const [isBusy, setIsBusy] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const cancelTokenRef = useRef<CancelTokenSource | null>(null)

  const methods = useForm({
    mode: 'onSubmit'
  })

  const {
    data: purchasesOptionData,
    isLoading: isLoadingData
  } = ClimaProgramQuery.getPurchasesOptionsData()

  useEffect(() => {
    if (id) {
      fetchFeedPurchases()
    }
  }, [id])

  async function fetchFeedPurchases() {
    setIsBusy(true)
    try {
      const response = await ClimaProgramService.editPurchase(id)
      const responseFile = await ClimaProgramService.getFile(id)
      methods.reset({
        ...response.data,
        purchaseDate: dayjs(response.data.purchaseDate).format('DD.MM.YYYY'),
        feedingFrom: dayjs(response.data.feedingFrom).format('DD.MM.YYYY'),
        feedingTo: dayjs(response.data.feedingTo).format('DD.MM.YYYY'),
        purchaseReceiptFile: responseFile?.data
          ? { data: responseFile.data, name: response.data.purchaseReceiptOriginalFilename }
          : null
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function onSubmit(data: FeedPurchaseData) {

    if (!data.purchaseReceiptFile) {
      notify(t('Please add feed purchase receipt'), 'error')
      return
    }
    data.farmerId = idFarmer
    const formData = createPurchaseFormData(data)
    const source = axios.CancelToken.source()
    cancelTokenRef.current = source
    setIsUploading(true)
    setUploadProgress(0)
    try {
      if (id) {
        await ClimaProgramService.updatePurchase({
          id: id,
          formData,
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent
            if (total) {
              setUploadProgress(Math.round((loaded / total) * 100))
            }
          },
          cancelToken: source
        })
        notify(t('Purchase updated successfully'), 'success')
      } else if (idFarmer) {
        await ClimaProgramService.createPurchase({
          formData,
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent
            if (total) {
              setUploadProgress(Math.round((loaded / total) * 100))
            }
          },
          cancelToken: source
        })
        notify(t('Purchase created successfully'), 'success')
      }
      navigate(buildCloseUrl())
    } catch (err) {
      handleErrors(err)
    }
    setIsUploading(false)
    setUploadProgress(0)
  }

  function renderActions() {
    return <El.PageHeaderActionButton>{id ? t('Update') : t('Save')}</El.PageHeaderActionButton>
  }

  function renderForm() {
    return <FormPart options={purchasesOptionData} />
  }

  function renderProgressBar() {
    return <Progress
      percent={uploadProgress}
      indicating
      progress
      style={{ marginBottom: '1rem' }}
    />
  }

  const farmerName = hash.startsWith('#farmerName=') ? decodeURIComponent(hash.replace('#farmerName=', '')) : ''

  return (
    <Layout
      title={id ? t('Edit purchase {{farmerName}}', { farmerName }) : t('Add purchase {{farmerName}}', { farmerName })}
      segmented
      isBusy={isLoadingData || isBusy}
      actions={renderActions()}
      onSubmit={methods.handleSubmit(onSubmit)}
      close={buildCloseUrl()}
    >

      {isUploading && renderProgressBar()}
      <FormProvider {...methods}>
        {renderForm()}
      </FormProvider>
    </Layout>
  )
}

export default EditFeedPurchases
